import { useState, useEffect } from "react";
import Icon from "@amzn/meridian/icon";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import { SourceAttribution } from "src/components/pages/home/store/types";
import openQuote from "src/images/open_quote.svg";
import ReactMarkdown from "react-markdown";
import "./citationText.scss";

interface CitationTextProps {
  text: string;
  citations?: SourceAttribution[];
}

const markdownComponents = {
  p: ({ children }: any) => <>{children}</>,
  ul: ({ children }: any) => <>{children}</>,
  ol: ({ children }: any) => <>{children}</>,
  li: ({ children }: any) => (
    <>
      <br />
      {children}
    </>
  ),
};

/**
 * Renders text with clickable citation links that show tooltips with source information
 */
const CitationText = ({ text, citations }: CitationTextProps) => {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [citationData, setCitationData] = useState<{
    citationNum: number;
    occurrenceIndex: number;
  } | null>(null);

  const handleCloseTooltip = () => {
    setActiveTooltip(null);
    setCitationData(null);
  };

  useEffect(() => {
    // Auto-close tooltip on scroll, resize, and click outside
    if (activeTooltip !== null) {
      document.addEventListener("click", handleCloseTooltip);
      document.addEventListener("scroll", handleCloseTooltip, true);
      window.addEventListener("resize", handleCloseTooltip);

      return () => {
        document.removeEventListener("click", handleCloseTooltip);
        document.removeEventListener("scroll", handleCloseTooltip, true);
        window.removeEventListener("resize", handleCloseTooltip);
      };
    }
  }, [activeTooltip]);

  const handleCitationClick = (
    index: number,
    citationNum: number,
    occurrenceIndex: number,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();

    if (activeTooltip === index) {
      setActiveTooltip(null);
      setCitationData(null);
      return;
    }

    // Get element's position and viewport dimensions
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 450; // matches CSS width
    const tooltipMaxHeight = viewportHeight * 0.8; // matches CSS max-height: 80vh

    // Calculate horizontal position (keep centered, but adjust if off-screen)
    let left = rect.left + rect.width / 2;
    if (left - tooltipWidth / 2 < 10) {
      left = tooltipWidth / 2 + 10; // Keep 10px margin from left edge
    } else if (left + tooltipWidth / 2 > viewportWidth - 10) {
      left = viewportWidth - tooltipWidth / 2 - 10; // Keep 10px margin from right edge
    }

    // Calculate vertical position (prefer above, but show below if not enough space)
    let top = rect.top - 10;
    if (top - tooltipMaxHeight < 10) {
      // Not enough space above, show below the citation link
      top = rect.bottom + 10;
    }

    setTooltipPosition({ top, left });
    setActiveTooltip(index);
    setCitationData({ citationNum, occurrenceIndex });
  };

  const getSnippetExcerpt = (
    citation: SourceAttribution,
    occurrenceIndex: number,
  ) => {
    if (
      !citation.textMessageSegments ||
      citation.textMessageSegments.length === 0
    ) {
      return citation.snippet;
    }

    const segment = citation.textMessageSegments[occurrenceIndex];
    if (!segment || !segment.snippetExcerpt) {
      return citation.snippet;
    }

    const excerptValues = Object.values(segment.snippetExcerpt);
    let text =
      excerptValues
        .filter((value) => value && value.trim())
        .join(" ")
        .replace(/\s+/g, " ")
        .trim() || citation.snippet;

    return text;
  };

  if (!text) {
    return <span></span>;
  }

  if (!citations || citations.length === 0) {
    return (
      <ReactMarkdown components={markdownComponents}>
        {text.replace(/\n\n/g, "\n")}
      </ReactMarkdown>
    );
  }

  // Parse citation markers like [1], [2] from text
  const citationRegex = /\[(\d+)\]/g;
  const parts = text.split(citationRegex);

  // Track multiple occurrences of same citation number
  const citationCounts: { [key: number]: number } = {};

  return (
    <div className="citation-text">
      {parts.map((part, index) => {
        // Split creates: ["Hello ", "1", " world ", "2", " test"] - odd indices are citation numbers
        if (index % 2 === 1) {
          const citationNum = parseInt(part);

          citationCounts[citationNum] = (citationCounts[citationNum] || 0) + 1;
          const occurrenceIndex = citationCounts[citationNum] - 1;

          return (
            <span key={index} className="citation-wrapper">
              <span
                className="citation-link"
                onClick={(e) =>
                  handleCitationClick(index, citationNum, occurrenceIndex, e)
                }
              >
                [{part}]
              </span>{" "}
            </span>
          );
        }
        const leadingWhitespace = part.match(/^[\s]*/)?.[0] || "";
        const stripped = part.replace(/^\s*\n+/, "").replace(/\n+\s*$/, "");
        const newlineCount = Math.min(
          (leadingWhitespace.match(/\n/g) || []).length,
          2,
        );
        const leadingBreaks = newlineCount;
        const hasEmphasis = /\*\*.+?\*\*|\*.+?\*/.test(stripped);
        return part ? (
          <span key={index}>
            {Array.from({ length: leadingBreaks }, (_, i) => (
              <br key={`br-${index}-${i}`} />
            ))}
            {hasEmphasis ? (
              <ReactMarkdown components={markdownComponents}>
                {stripped.replace(/\n\n/g, "\n").replace(/\n/g, "  \n")}
              </ReactMarkdown>
            ) : (
              stripped
            )}
          </span>
        ) : null;
      })}

      {activeTooltip !== null && citationData && (
        <div
          className="citation-tooltip"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform:
              tooltipPosition.top > window.innerHeight / 2
                ? "translate(-50%, -100%)"
                : "translate(-50%, 0)",
          }}
          onClick={(e) => e.stopPropagation()}
          role="tooltip"
        >
          <div className="citation-tooltip-content">
            <div className="citation-header">
              <div className="citation-header-content">
                {(() => {
                  const citation = citations.find(
                    (c) => c.citationNumber === citationData.citationNum,
                  );
                  if (citation && citation.url) {
                    return (
                      <div className="citation-url">
                        <Icon
                          className="document-icon"
                          tokens={documentTokens}
                          color="primary"
                        />
                        <a
                          href={citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="url-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {citation.title || citation.url}
                        </a>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              <button
                className="close-button"
                onClick={handleCloseTooltip}
                aria-label="Close tooltip"
              >
                <Icon tokens={closeMediumTokens} color="secondary" />
              </button>
            </div>

            <div className="citation-content">
              <img className="quote-icon" src={openQuote} alt="open quote" />
              <p>
                {(() => {
                  const citation = citations.find(
                    (c) => c.citationNumber === citationData.citationNum,
                  );
                  const text = citation
                    ? getSnippetExcerpt(citation, citationData.occurrenceIndex)
                    : "";
                  return text ? `${text}...` : "";
                })()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitationText;
